import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useSelectionStore } from '@/stores/ui/selection';
import {
  transformTargetInGroupSpace,
  updateLinearArrayVector3,
  updatePolarArrayRotation,
  updatePolarArrayVector3,
  updateSymmetryRotation,
  updateSymmetryVector3,
} from '@/threejs/objects/group';
import { useDataValidation } from './useDataValidation';

/**
 * Composable for baking group arrays and symmetry into individual objects
 */
export const useBakeGroup = () => {
  const cruxModuleStore = useCruxModuleStore();
  const selectionStore = useSelectionStore();
  const { sanitizeModuleData } = useDataValidation();

  /**
   * Deep clone an object, removing clone-specific properties
   */
  const cloneModuleData = (module) => {
    const cloned = JSON.parse(JSON.stringify(module));

    // Remove clone-specific properties
    delete cloned.code;
    delete cloned.clone;
    delete cloned.clonedCode;
    if (cloned.data) {
      delete cloned.data.clonedCode;
    }

    return cloned;
  };

  /**
   * Bake array/symmetry items into real permanent objects
   */
  const bakeGroupItems = async () => {
    // Get the currently selected group
    const groupNodeId = selectionStore.selectedObjectId;

    if (!groupNodeId) {
      console.warn('No object selected');
      return;
    }

    const groupModule = cruxModuleStore.cruxModules.find((m) => m.code === groupNodeId);

    if (!groupModule) {
      console.warn('Selected object not found in cruxModules');
      return;
    }

    if (groupModule.type !== 'group') {
      console.warn('Selected object is not a group');
      return;
    }

    const groupContext = groupModule.data;
    const groupMode = groupContext.groupMode;

    if (groupMode !== 'symmetry' && groupMode !== 'linear_array' && groupMode !== 'polar_array') {
      console.warn('Can only bake symmetry, linear_array, or polar_array groups');
      return;
    }

    // Get all children of this group
    const children = [...cruxModuleStore.getSceneTreeModules(groupNodeId)];

    if (!children || children.length === 0) {
      console.warn('No children found to bake');
      return;
    }

    const groupPosition = groupModule.data.position || { x: 0, y: 0, z: 0 };

    // Duplicate children within the same group (no new groups created)
    if (groupMode === 'symmetry') {
      for (const child of children) {
        const clonedData = cloneModuleData(child);
        const position = child.data.position || { x: 0, y: 0, z: 0 };

        clonedData.data.position = updateSymmetryVector3(position, groupContext);
        if (child.data.target) {
          clonedData.data.target = transformTargetInGroupSpace(
            child.data.target,
            groupPosition,
            updateSymmetryVector3,
            groupContext
          );
        }
        if (child.data.rotation) {
          clonedData.data.rotation = updateSymmetryRotation(child.data.rotation, groupContext);
        }

        clonedData.data.parentCode = groupNodeId;
        clonedData.name = `${child.name} (Mirrored)`;
        clonedData.type = child.type;

        await cruxModuleStore.store(sanitizeModuleData(clonedData));
      }
    } else if (groupMode === 'linear_array') {
      const arrayCount = groupContext.arrayCount || 3;

      for (let i = 1; i < arrayCount; i++) {
        for (const child of children) {
          const clonedData = cloneModuleData(child);
          const position = child.data.position || { x: 0, y: 0, z: 0 };

          clonedData.data.position = updateLinearArrayVector3(position, groupContext, i);
          if (child.data.target) {
            clonedData.data.target = transformTargetInGroupSpace(
              child.data.target,
              groupPosition,
              updateLinearArrayVector3,
              groupContext,
              i
            );
          }

          clonedData.data.parentCode = groupNodeId;
          clonedData.name = `${child.name} (${i})`;
          clonedData.type = child.type;

          await cruxModuleStore.store(sanitizeModuleData(clonedData));
        }
      }
    } else if (groupMode === 'polar_array') {
      const polarCount = groupContext.polarCount || 6;

      for (let i = 1; i < polarCount; i++) {
        for (const child of children) {
          const clonedData = cloneModuleData(child);
          const position = child.data.position || { x: 0, y: 0, z: 0 };

          clonedData.data.position = updatePolarArrayVector3(position, groupContext, i);
          if (child.data.target) {
            clonedData.data.target = transformTargetInGroupSpace(
              child.data.target,
              groupPosition,
              updatePolarArrayVector3,
              groupContext,
              i
            );
          }
          if (child.data.rotation) {
            clonedData.data.rotation = updatePolarArrayRotation(child.data.rotation, groupContext, i);
          }

          clonedData.data.parentCode = groupNodeId;
          clonedData.name = `${child.name} (${i})`;
          clonedData.type = child.type;

          await cruxModuleStore.store(sanitizeModuleData(clonedData));
        }
      }
    }

    // Update the original group back to normal mode
    const updatedGroupData = sanitizeModuleData({
      ...groupModule,
      data: {
        ...groupModule.data,
        groupMode: 'normal',
      },
    });
    await cruxModuleStore.update(groupNodeId, updatedGroupData);
  };

  return {
    bakeGroupItems,
  };
};
